import { type FC } from 'react';
import { useDispatch } from 'react-redux';
import { BsPlus } from 'react-icons/bs';
import { DragDropContext } from 'react-beautiful-dnd';
import DelayedDroppableWrapper from '@editor2/components/DelayedDroppableWrapper';

import CopyInput from '@editor2/components/CopyInputWrapper';
import IconTextButton from '@editor2/components/IconTextButton';
import Metadata from '@editor2/components/Metadata';
import { useDebouncedDispatch } from '@editor2/hooks/useDebouncedDispatch';
import { updateElement } from '@editor2/reducers/editor/editorSlice';
import { getNewElement } from '@editor2/services/elementService';
import { elementCommonStyles } from '@editor2/styles/element';
import { type MCQuestionPayload, type MCQuestionPool } from '@editor2/types/pageElements';

import PoolQuestion from './PoolQuestion';
import { questionBlockLabel } from './styles';

interface Props {
	element: MCQuestionPool;
	courseId: number;
}

const MCQuestionPoolElement: FC<Props> = (props) => {
	const { element, courseId } = props;

	const commonPayload = { elementId: element.family_id, courseId };
	const dispatch = useDispatch();
	const debouncedDispatch = useDebouncedDispatch();

	const onChange = (value) => {
		dispatch(updateElement({ ...commonPayload, ...value }));
	};
	const onDebouncedChange = (value) => {
		debouncedDispatch(updateElement({ ...commonPayload, ...value }));
	};

	const handleAddQuestionClick = () => {
		const newQuestion = getNewElement('MCQuestion') as MCQuestionPayload;
		onChange({ questions: [...element.questions, newQuestion] });
	};

	const handleDragEnd = (result) => {
		const { source, destination, type } = result;
		const { index: sourceIndex, droppableId: sourceDroppableId } = source;
		const { index: destinationIndex, droppableId: destinationDroppableId } = destination;

		if (type === 'PoolQuestion' && sourceDroppableId === destinationDroppableId) {
			const questions = [
				...element.questions.slice(0, sourceIndex),
				...element.questions.slice(sourceIndex + 1)
			];
			onChange({
				questions: [
					...questions.slice(0, destinationIndex),
					element.questions[sourceIndex],
					...questions.slice(destinationIndex)
				]
			});
		}
	};

	return (
		<div className={elementCommonStyles}>
			<CopyInput label="Family ID" value={element.family_id} />

			<Metadata
				courseId={courseId}
				metadata={element.metadata}
				onDebouncedChange={onDebouncedChange}
			/>

			<div className={questionBlockLabel}>Pool Questions:</div>
			<DragDropContext onDragEnd={handleDragEnd}>
				<DelayedDroppableWrapper droppableId={element.family_id} type="PoolQuestion">
					{(provided) => (
						<div ref={provided.innerRef} {...provided.droppableProps}>
							{element.questions.map((question, index) => (
								<PoolQuestion
									question={question}
									key={question.family_id}
									index={index}
									element={element}
									courseId={courseId}
								/>
							))}

							{provided.placeholder}
						</div>
					)}
				</DelayedDroppableWrapper>
			</DragDropContext>

			<IconTextButton icon={<BsPlus />} onClick={handleAddQuestionClick}>
				Add Question
			</IconTextButton>
		</div>
	);
};

export default MCQuestionPoolElement;
